import { useRef, useState, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import {
  SearchBox,
  useSearchBox,
  type UseSearchBoxProps
} from 'react-instantsearch'
import { _x } from '@wordpress/i18n'

const searchBoxclassNameNames = {
  form: 'search-filter-form search-filter-form--responsive',
  input: 'search-filter-input',
  submit: 'search-filter-button',
  reset: 'search-filter-button',
  submitIcon: 'search-filter-submit-icon',
  resetIcon: 'search-filter-reset-icon'
}

function SearchFilter(props: { placeholder?: string }) {
  return (
    <SearchBox
      classNames={searchBoxclassNameNames}
      placeholder={props.placeholder}
    />
  )
}

const debounceMs = 400

export function CustomSearchFilter(
  props: UseSearchBoxProps & {
    renderBefore?: React.ReactNode
    placeholder?: string
  }
) {
  const { renderBefore, placeholder, ...restProps } = props
  const { query, refine } = useSearchBox(restProps)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState<string>(query)

  const debouncedRefine = useMemo(
    () => debounce((q: string) => refine(q), debounceMs),
    [refine]
  )

  useEffect(() => {
    setInputValue(query)
    return () => debouncedRefine.cancel()
  }, [query, debouncedRefine])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const v = event.currentTarget.value
    setInputValue(v)
    debouncedRefine(v)
  }

  const onReset = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
    debouncedRefine.cancel()
    setInputValue('')
    refine('')
    inputRef.current?.focus()
  }

  const onSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
    debouncedRefine.flush()
  }

  return (
    <form
      action=""
      className="search-filter-form search-filter-form--responsive"
      noValidate={true}
      // biome-ignore lint/a11y/useSemanticElements:
      role="search"
      onReset={onReset}
      onSubmit={onSubmit}
    >
      {props.renderBefore}
      <input
        ref={inputRef}
        className="search-filter-input"
        aria-label="Search"
        autoCapitalize="off"
        placeholder={placeholder}
        spellCheck={false}
        maxLength={512}
        type="search"
        value={inputValue}
        onChange={handleInputChange}
      />
      <button
        className="search-filter-button"
        type="submit"
        title={_x(
          'Submit the search query',
          'Search Filter Submit Button Title',
          'icf'
        )}
      >
        <svg
          className="search-filter-submit-icon"
          width="10"
          height="10"
          viewBox="0 0 40 40"
          aria-hidden="true"
        >
          <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z" />
        </svg>
      </button>
      <button
        className="search-filter-button"
        type="reset"
        title={_x(
          'Clear the search query',
          'Search Filter Clear Button Title',
          'icf'
        )}
        hidden={inputValue.length === 0}
      >
        <svg
          className="ais-SearchBox-resetIcon search-filter-reset-icon"
          viewBox="0 0 20 20"
          width="10"
          height="10"
          aria-hidden="true"
        >
          <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z" />
        </svg>
      </button>
    </form>
  )
}

export default SearchFilter
